// API wrapper with retry, timeout, abort, and request deduplication

const BASE = '';
const DEFAULT_TIMEOUT = 15000;
const MAX_RETRIES = 2;
const RETRY_DELAY = 1000;

// In-flight request deduplication for GET requests
const inflightRequests = new Map();

function isRetryable(status) {
  return status === 0 || status === 408 || status === 429 || status >= 500;
}

async function fetchWithTimeout(url, options, timeoutMs = DEFAULT_TIMEOUT) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const resp = await fetch(url, { ...options, signal: controller.signal });
    return resp;
  } finally {
    clearTimeout(id);
  }
}

async function fetchWithRetry(url, options, { retries = MAX_RETRIES, timeout = DEFAULT_TIMEOUT } = {}) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const resp = await fetchWithTimeout(url, options, timeout);
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({ error: resp.statusText }));
        // Don't retry client errors (except 408/429)
        if (!isRetryable(resp.status) || attempt === retries) {
          throw { status: resp.status, ...data };
        }
        lastError = { status: resp.status, ...data };
      } else {
        return resp;
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        lastError = { status: 0, error: 'Request timed out' };
        if (attempt === retries) throw lastError;
      } else if (e.status) {
        // Already formatted error from above
        throw e;
      } else {
        lastError = { status: 0, error: 'Network error' };
        if (attempt === retries) throw lastError;
      }
    }
    // Exponential backoff before retry
    if (attempt < retries) {
      await new Promise(r => setTimeout(r, RETRY_DELAY * Math.pow(2, attempt)));
    }
  }
  throw lastError;
}

export async function apiGet(path, { timeout, retries, deduplicate = true } = {}) {
  const url = `${BASE}${path}`;

  // Deduplicate concurrent identical GET requests
  if (deduplicate && inflightRequests.has(url)) {
    return inflightRequests.get(url);
  }

  const promise = (async () => {
    try {
      const resp = await fetchWithRetry(url, { credentials: 'include' }, { timeout, retries });
      return resp.json();
    } finally {
      inflightRequests.delete(url);
    }
  })();

  if (deduplicate) {
    inflightRequests.set(url, promise);
  }
  return promise;
}

export async function apiPost(path, body, { timeout } = {}) {
  const resp = await fetchWithRetry(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  }, { timeout, retries: 0 }); // No retries for mutations
  return resp.json();
}

export async function apiDelete(path, { timeout } = {}) {
  const resp = await fetchWithRetry(`${BASE}${path}`, {
    method: 'DELETE',
    credentials: 'include',
  }, { timeout, retries: 0 });
  return resp.json();
}
